import { HalloweenArt } from "../../../../theme/HalloweenArt";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { getHistoryUser } from "../../../../api/order.api";
import { getCafeStatus } from "../../../../api/cafe.api";
import { State } from "../../../../reducer";
import { User } from "../../../../interfaces/user";
import { ikThumb } from "../../../../utils/image";

const TYPE_META: Record<string, { emoji: string; label: string }> = {
  hot:    { emoji: "🔥", label: "Hot" },
  cold:   { emoji: "🧊", label: "Cold" },
  frappe: { emoji: "🥤", label: "Frappe" },
  "extra-shot":  { emoji: "⚡", label: "Extra shot" },
  "reduce-shot": { emoji: "🪶", label: "Reduce shot" },
};

const RANGE_OPTIONS = [
  { id: "7",  label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "90", label: "Last 90 days" },
  { id: "all", label: "All time" },
] as const;

type RangeId = typeof RANGE_OPTIONS[number]["id"];

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  done:     { label: "Done",      cls: "bg-emerald-100 text-emerald-900" },
  picked:   { label: "Picked up", cls: "bg-emerald-100 text-emerald-900" },
  cancel:   { label: "Cancelled", cls: "bg-ink-100 text-ink-700" },
  rejected: { label: "Rejected",  cls: "bg-red-100 text-red-900" },
};

export default function UserHistoryTable() {
  const user = useSelector((state: State) => state.user);
  const dispatch = useDispatch();
  const [range, setRange] = useState<RangeId>("30");

  const { startDate, endDate } = useMemo(() => {
    if (range === "all") return { startDate: "", endDate: "" };
    const days = Number(range);
    return {
      startDate: moment().subtract(days, "days").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
    };
  }, [range]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["getHistoryUser", startDate, endDate],
    queryFn: () => getHistoryUser(undefined, startDate, endDate),
    refetchInterval: 5000,
  });

  useEffect(() => {
    getCafeStatus().then((result) => {
      const responsepost: User = {
        login: true, sub: "auth_google",
        id: user?.id, email: user?.email, name: user?.name, role: user?.role,
        isBarOpen: result?.isBarOpen, isCafeOpen: result?.isCafeOpen,
      };
      dispatch({ type: "LOGIN", payload: responsepost });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(() => {
    if (!data) return [];
    const filtered = data.filter((e) =>
      ["picked", "cancel", "rejected", "done"].includes(e.status)
    );
    // @ts-ignore
    return filtered.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [data]);

  // group by day for nice section headers
  const grouped = useMemo(() => {
    const groups = new Map<string, typeof items>();
    items.forEach((it) => {
      const day = moment(it.updatedAt).format("YYYY-MM-DD");
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day)!.push(it);
    });
    return Array.from(groups.entries());
  }, [items]);

  return (
    <div className="min-h-screen bg-white font-sans pb-[64px]">
      {/* Hero */}
      <header className="px-5 pt-[24px] pb-[16px]">
        <p className="eyebrow-v2 mb-2">Your history</p>
        <h1 className="font-display font-black text-[30px] leading-[1.1] tracking-tightest text-ink-1000">
          {items.length === 0 && !isLoading
            ? "No orders yet"
            : `${items.length} ${items.length === 1 ? "order" : "orders"}`}
        </h1>
        <p className="text-sm text-ink-500 mt-2">
          {range === "all" ? "All time" : RANGE_OPTIONS.find((r) => r.id === range)?.label}
        </p>
      </header>

      {/* Range chips — sticky */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-ink-100">
        <div className="px-5 py-3 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2">
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRange(r.id)}
                disabled={isLoading}
                className={`whitespace-nowrap ${range === r.id ? "chip-v2-active" : "chip-v2"} ${isLoading ? "opacity-50" : ""}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="px-5 pt-[20px]">
        {/* Loading */}
        {isLoading && (
          <div className="py-[80px] flex justify-center">
            <svg className="w-6 h-6 animate-spin text-ink-700" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
              <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="py-[64px] text-center">
            <p className="font-display font-black text-lg text-ink-1000">Failed to load history</p>
            <p className="text-sm text-ink-500 mt-1">{(error as any)?.message ?? "Try again later"}</p>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !isError && items.length === 0 && (
          <div className="py-[64px] flex flex-col items-center text-center">
            <div className="seasonal-empty-icon w-[64px] h-[64px] rounded-full bg-ink-100 flex items-center justify-center mb-4 text-3xl">
              📭
            </div>
            <HalloweenArt variant="ghost-coffee" className="art-empty" />
            <p className="font-display font-black text-lg text-ink-1000">No history found</p>
            <p className="text-sm text-ink-500 mt-1">Try a different date range</p>
          </div>
        )}

        {/* List grouped by day */}
        {!isLoading && !isError && grouped.map(([day, orders]) => (
          <section key={day} className="mb-6">
            <p className="eyebrow-v2 mb-3">{moment(day).format("dddd, MMM D")}</p>
            <div className="card-v2 divide-y divide-ink-100 overflow-hidden">
              {orders.map((o) => {
                const typeMeta = TYPE_META[o.type] ?? { emoji: "", label: o.type };
                const pill = STATUS_PILL[o.status] ?? STATUS_PILL.cancel;
                const isCancelLike = o.status === "cancel" || o.status === "rejected";
                // @ts-ignore — backend now returns cancelledBy relation
                const cancelledBy = o.cancelledBy as { id: number; name: string; role?: string } | null | undefined;
                const cancelLabel = !isCancelLike
                  ? null
                  : cancelledBy
                    ? cancelledBy.id === user?.id
                      ? "Cancelled by you"
                      : `Cancelled by ${cancelledBy.name}${cancelledBy.role && cancelledBy.role !== "user" ? ` (${cancelledBy.role})` : ""}`
                    : o.status === "rejected"
                      ? "Rejected by barista"
                      : "Cancelled";

                const channel = o.menu?.category?.categoryFor;
                const isDessert = channel === "dessert";
                const fallbackEmoji = isDessert ? "🍰" : channel === "bar" ? "🍸" : "☕";
                const menuImg = o.menu?.image as string | null | undefined;
                const initial = (o.menu?.name ?? "?").trim().slice(0, 1).toUpperCase();
                return (
                  <div key={o.id} className="px-[16px] py-[14px] flex items-start gap-3">
                    {/* Menu image */}
                    <div className="flex-shrink-0 w-[56px] h-[56px] rounded-2xl bg-ink-100 overflow-hidden flex items-center justify-center">
                      {menuImg ? (
                        <img
                          src={ikThumb(menuImg, 160, 160)}
                          alt={o.menu.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span className="font-display font-black text-xl text-ink-300" aria-hidden>
                          {initial || fallbackEmoji}
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="font-display font-bold text-base text-ink-1000 truncate">
                          {o.menu.name}
                        </p>
                        <span className="text-[11px] text-ink-400 font-medium flex-shrink-0">
                          {moment(o.updatedAt).format("HH:mm")}
                        </span>
                      </div>
                      <p className="text-xs text-ink-500 mt-0.5">
                        {isDessert
                          ? `🍰 ${o.menu?.category?.name ?? "Dessert"}`
                          : `${typeMeta.emoji} ${typeMeta.label}${channel === "cafe" ? ` · ${o.sweetness}% sweet` : ""}`}
                        {o.remark && o.remark !== "-" && ` · "${o.remark}"`}
                      </p>

                      {/* Status row */}
                      <div className="flex items-center flex-wrap gap-2 mt-2">
                        <span className={`px-2.5 h-[22px] inline-flex items-center rounded-full text-[10px] font-bold ${pill.cls}`}>
                          {pill.label}
                        </span>
                        {cancelLabel && (
                          <span className="text-[11px] text-ink-500">
                            {cancelLabel}
                          </span>
                        )}
                        {o.rejectNote && (
                          <span className="text-[11px] text-red-700 italic">
                            "{o.rejectNote}"
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
